import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { 
  Leaf, 
  Target, 
  Users, 
  Award,
  ArrowRight,
  Recycle
} from 'lucide-react';

export default function About() {
  return (
    <div data-testid="about-page" className="min-h-screen">
      <SEO 
        title="About AfriCycle Zambia | Our Mission & Leadership"
        description="Learn about AfriCycle Zambia, led by Dr. Kachikoti Banda. We are Zambia's leading EPR compliance organization for plastic packaging and WEEE recycling. Discover our mission, vision, and approach."
        keywords="AfriCycle Zambia about, Dr Kachikoti Banda, EPR Zambia leadership, plastic recycling Zambia company, environmental compliance Africa"
        canonicalUrl="https://africycle.pro/about"
      />
      {/* Hero Section */}
      <section className="bg-[#D2E4D6]/30 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-6">
              <Leaf className="h-5 w-5 text-[#69A488]" />
              <span className="text-[#69A488] font-medium text-sm">About Us</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#056839] leading-tight mb-6">
              ABOUT AFRICYCLE ZAMBIA
            </h1>
            <p className="text-lg text-[#3F3F3F] leading-relaxed">
              Advancing circular economy solutions through Extended Producer Responsibility
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <div className="bg-[#F9FAFB] rounded-2xl p-8 mb-12">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#D2E4D6] flex items-center justify-center flex-shrink-0">
                  <Recycle className="h-6 w-6 text-[#056839]" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-[#056839] mb-4 mt-0">OUR MISSION</h2>
                  <p className="text-[#3F3F3F] leading-relaxed m-0">
                    AfriCycle Zambia is a Producer Responsibility Organisation advancing circular economy solutions 
                    through Extended Producer Responsibility (EPR). We connect producers, recyclers, and waste collectors 
                    to build a transparent, digitally enabled system for waste recovery and compliance.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="text-center p-6 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-full bg-[#D2E4D6] flex items-center justify-center mx-auto mb-4">
                  <Target className="h-6 w-6 text-[#056839]" />
                </div>
                <h3 className="text-lg font-semibold text-[#056839] mb-2">VISION</h3>
                <p className="text-sm text-[#6B7280]">
                  A sustainable Zambia where plastic waste is minimized and responsibly managed
                </p>
              </div>
              
              <div className="text-center p-6 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-full bg-[#D2E4D6] flex items-center justify-center mx-auto mb-4">
                  <Users className="h-6 w-6 text-[#056839]" />
                </div>
                <h3 className="text-lg font-semibold text-[#056839] mb-2">APPROACH</h3>
                <p className="text-sm text-[#6B7280]">
                  Collaborative partnerships connecting producers, recyclers, and waste collectors
                </p>
              </div>
              
              <div className="text-center p-6 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-full bg-[#D2E4D6] flex items-center justify-center mx-auto mb-4">
                  <Award className="h-6 w-6 text-[#056839]" />
                </div>
                <h3 className="text-lg font-semibold text-[#056839] mb-2">EXCELLENCE</h3>
                <p className="text-sm text-[#6B7280]">
                  Deep technical expertise in environmental engineering and waste management
                </p>
              </div>
            </div>

            {/* Leadership / Managing Director Section */}
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-[#056839] mb-8 tracking-wide">LEADERSHIP</h2>
              <div className="flex flex-col md:flex-row gap-8 items-start">
                {/* Round Photo */}
                <div className="flex-shrink-0">
                  <img 
                    src="/dr-kachikoti-banda.png"
                    alt="Dr. Kachikoti Banda - Managing Director of AfriCycle Zambia EPR Compliance"
                    className="w-48 h-48 md:w-56 md:h-56 rounded-full object-cover object-top border-4 border-[#D2E4D6] shadow-lg"
                  />
                </div>
                {/* Text Content */}
                <div className="flex-1">
                  <h3 className="text-2xl md:text-3xl font-bold text-[#3F3F3F] mb-2">DR. KACHIKOTI BANDA</h3>
                  <p className="text-[#056839] font-medium text-lg italic mb-6">Managing Director</p>
                  <p className="text-[#3F3F3F] leading-relaxed text-base">
                    Kachikoti Banda is an Environmental Engineering and Public Health expert with a Ph.D. in Environmental 
                    Engineering and over a decade of experience in waste management systems, circular economy, and policy 
                    development. As Managing Director at AfriCycle Zambia, he provides strategic leadership, driving initiatives 
                    aligned with sustainable waste solutions and Extended Producer Responsibility (EPR) implementation in Zambia. 
                    His work focuses on stakeholder engagement, regulatory development, and capacity building, supporting both 
                    public and private sector actors in advancing environmental sustainability.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#D2E4D6]/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-semibold text-[#056839] mb-4">
            READY TO START YOUR COMPLIANCE JOURNEY?
          </h2>
          <p className="text-[#3F3F3F] mb-8 max-w-xl mx-auto">
            Join the growing network of Zambian businesses committed to sustainable packaging practices.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/services">
              <Button className="btn-primary text-lg px-8">
                View Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button className="btn-outline text-lg px-8">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
